<template>
  <div
    v-if="showShift || canSeeShift"
    :id="`shift-${shift.id}`"
    :class="{
      'is-my-shift': isMyShift,
      'was-my-shift': wasMyShift,
      'type-nobody': currentAssignment === undefined,
      'volunteered-for-shift': volunteeredForShift,
      'volunteer-declined-for-shift': volunteerDeclinedForShift,
      [`status-${shift.coverRequestStatus}`]: 1,
      highlighted: isHighlighted,
    }"
  >
    <div v-if="showDate" class="date mb-2">
      <strong>{{ getShiftDate }}</strong>
    </div>
    <div class="row">
      <template v-if="canShowCoverShiftButton">
        <div class="col-sm-3 mt-sm-0 mt-1 cover-button">
          <button
            class="btn self-assign d-block btn-outline-secondary"
            type="button"
            @click="$emit('rota-shift-self-assign', shift)"
          >
            <font-awesome-icon fixed-width :icon="['far', 'hand-paper']" />
            {{ coverShiftLabel }}
          </button>
        </div>
      </template>
      <div class="col order-first order-sm-last">
        <div
          v-if="canShowStatus && getShiftCoverRequestText.length > 0"
          class="cover-request-status"
        >
          <span :class="getShiftCoverRequestClass" class="text-wrap mb-2">
            <font-awesome-icon
              v-if="getShiftCoverRequestIcon && getShiftCoverRequestText"
              :icon="getShiftCoverRequestIcon"
              class="me-1"
            />
            {{ getShiftCoverRequestText }}
          </span>
        </div>
        <div
          v-if="volunteeredForShift"
          class="cover-request-volunteered small text-muted"
        >
          Volunteered for shift at
          {{ formatModifiedDateLong(volunteeredForShift.timestamp) }}
        </div>
        <rota-agenda-shift-view
          :rota-type="rotaType"
          :rota-status="rotaStatus"
          :rota-id="rotaId"
          :shift="shift"
          :user-id="authStore.user.id"
          :always-expanded="true"
          :hide-assigned="!showShift"
          :activities="activities"
          :location="location"
        />
      </div>
    </div>
  </div>
  <div v-else class="shift not-visible"></div>
</template>

<script>
import { computed } from "vue";
import has from "lodash/has";
import { useAuthStore } from "@switchsystems/coactive-vue-lib";
import RotaAgendaShiftView from "@/components/rota/agenda/RotaAgendaShiftView.vue";
import {
  SHIFTCOVER_STATUS_PENDING,
  SHIFTCOVER_STATUS_OPEN,
  SHIFTCOVER_STATUS_VOLUNTEERED,
  SHIFTCOVER_STATUS_DECLINED,
  SHIFTCOVER_STATUS_APPROVED,
  SHIFTCOVER_STATUS_REJECTED,
  shiftCoverRequestStatuses,
} from "@/lib/constants/shift-cover-requests";
import {
  getShiftPreviousAssignment,
  getShiftCurrentAssignment,
  getIsMyShift,
  getIsMyAgencyShift,
  getWasMyShift,
  getVolunteeredForShift,
  getVolunteerDeclinedForShift,
  canSeeShiftCover,
} from "@/lib/rota/shift-functions";
import {
  formatDateAsDay,
  formatRotaStartDateLong,
  formatModifiedDateLong,
} from "@/lib/date-functions";

export default {
  name: "RotaShiftWithCoverRequest",
  components: {
    RotaAgendaShiftView,
  },
  props: {
    shift: {
      type: Object,
      required: true,
    },
    rotaType: {
      type: String,
      default() {
        return "";
      },
    },
    rotaStatus: {
      type: String,
      default() {
        return "";
      },
    },
    rotaId: {
      type: String,
      default() {
        return "";
      },
    },
    activities: {
      type: [Boolean, Array],
      default: () => [],
    },
    location: {
      type: String,
      default() {
        return "";
      },
    },
    showShift: {
      type: Boolean,
      default() {
        return false; // use canSeeShift to determine if we can see the shift details
      },
    },
    showDate: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  emits: ["rota-shift-self-assign"],
  setup(props) {
    const authStore = useAuthStore();

    const prevAssignment = computed(() =>
      getShiftPreviousAssignment(props.shift),
    );
    const currentAssignment = computed(() =>
      getShiftCurrentAssignment(props.shift),
    );
    const isMyShift = computed(() => {
      if (authStore.isAgencyUser) {
        return getIsMyAgencyShift(props.shift, authStore.user?.agencyId);
      }
      return getIsMyShift(props.shift, authStore.user?.id);
    });
    const wasMyShift = computed(() => {
      if (authStore.isAgencyUser) {
        return false;
      }
      return getWasMyShift(props.shift, authStore.user?.id);
    });
    const volunteeredForShift = computed(
      () =>
        (!authStore.isAgencyUser &&
          getVolunteeredForShift(props.shift, authStore.user?.id)) ||
        volunteerDeclinedForShift.value,
    );
    const volunteerDeclinedForShift = computed(
      () =>
        !authStore.isAgencyUser &&
        getVolunteerDeclinedForShift(props.shift, authStore.user?.id),
    );
    const canSeeShift = computed(() => {
      return (
        isMyShift.value ||
        wasMyShift.value ||
        canSeeShiftCover(props.shift, authStore.user?.id) ||
        volunteeredForShift.value
      );
    });
    const getShiftDate = computed(() => {
      if (props.rotaStatus === "master") {
        return `${formatDateAsDay(props.shift.startDate)}`;
      }
      return `${formatRotaStartDateLong(props.shift.startDate)}`;
    });

    const canShowCoverShiftButton = computed(() => {
      // I can't cover my own shifts
      if (isMyShift.value) {
        return false;
      }
      if (authStore.isAgencyUser) {
        return false;
      }
      if (props.shift.coverNotRequired === true) {
        return false;
      }
      // coverRequestStatus is not open or volunteered
      if (
        props.shift.coverRequestStatus !== undefined &&
        ![SHIFTCOVER_STATUS_OPEN, SHIFTCOVER_STATUS_VOLUNTEERED].includes(
          props.shift.coverRequestStatus,
        )
      ) {
        return false;
      }
      // have I already volunteered for this shift?
      if (volunteeredForShift.value) {
        return false;
      }
      // not open for cover, and has a current assignment
      if (
        props.shift.coverRequestStatus == undefined &&
        currentAssignment.value !== undefined
      ) {
        return false;
      }
      // not open for cover, and was my shift
      if (
        props.shift.coverRequestStatus == undefined &&
        currentAssignment.value === undefined &&
        wasMyShift.value
      ) {
        return false;
      }
      return true;
    });

    const canShowStatus = computed(() => {
      if (authStore.isAgencyUser) {
        return false;
      }
      if (isMyShift.value) {
        return true;
      }
      if (props.shift.coverRequestStatus == SHIFTCOVER_STATUS_PENDING) {
        if (isMyShift.value) {
          return true;
        }
      }
      if (props.shift.coverRequestStatus == SHIFTCOVER_STATUS_OPEN) {
        return true;
      }
      if (props.shift.coverRequestStatus == SHIFTCOVER_STATUS_VOLUNTEERED) {
        return true;
      }
      if (volunteerDeclinedForShift.value) {
        return true;
      }
      if (volunteeredForShift.value) {
        return true;
      }
      if (props.shift.coverRequestStatus == SHIFTCOVER_STATUS_APPROVED) {
        if (isMyShift.value) {
          return true;
        }
        if (wasMyShift.value) {
          return true;
        }
      }
      if (props.shift.coverRequestStatus == SHIFTCOVER_STATUS_REJECTED) {
        if (isMyShift.value) {
          return true;
        }
        if (volunteeredForShift.value) {
          return true;
        }
      }
      return false;
    });

    const coverShiftLabel = computed(() => {
      if (!has(props.shift, "assignedTo")) {
        return "Cover this shift";
      }
      const lastEntry = currentAssignment.value;
      if (lastEntry?.type) {
        return `Cover ${lastEntry.displayName}'s shift`;
      }
      return "Cover this shift";
    });

    const getShiftCoverRequestClass = computed(() => {
      let definition = shiftCoverRequestStatuses.find(
        (e) => e.value == props.shift.coverRequestStatus,
      );
      if (getVolunteerDeclinedForShift(props.shift, authStore.user?.id)) {
        definition = shiftCoverRequestStatuses.find(
          (e) => e.value == SHIFTCOVER_STATUS_DECLINED,
        );
      }
      if (definition.volunteeredClass) {
        // if we are have volunteered for this shift, then use the volunteered prop
        if (
          volunteeredForShift.value &&
          !isMyShift.value &&
          !wasMyShift.value
        ) {
          return definition.volunteeredClass;
        }
      }
      return definition?.class || "";
    });

    const getShiftCoverRequestIcon = computed(() => {
      let definition = shiftCoverRequestStatuses.find(
        (e) => e.value == props.shift.coverRequestStatus,
      );
      if (getVolunteerDeclinedForShift(props.shift, authStore.user?.id)) {
        definition = shiftCoverRequestStatuses.find(
          (e) => e.value == SHIFTCOVER_STATUS_DECLINED,
        );
      }
      if (definition.volunteeredIcon) {
        // if we are have volunteered for this shift, then use the volunteered prop
        if (
          volunteeredForShift.value &&
          !isMyShift.value &&
          !wasMyShift.value
        ) {
          return definition.volunteeredIcon;
        }
      }
      return definition?.icon;
    });

    const getShiftCoverRequestText = computed(() => {
      let definition = shiftCoverRequestStatuses.find(
        (e) => e.value == props.shift.coverRequestStatus,
      );
      if (getVolunteerDeclinedForShift(props.shift, authStore.user?.id)) {
        definition = shiftCoverRequestStatuses.find(
          (e) => e.value == SHIFTCOVER_STATUS_DECLINED,
        );
      }
      if (!definition) {
        return "";
      }
      let labelProperty = "label";
      let templateProperty = "template";

      if (isMyShift.value) {
        labelProperty = "assignedLabel";
        templateProperty = "assignedTemplate";
        if (prevAssignment.value?.displayName === undefined) {
          templateProperty = "assignedPrevUnassignedTemplate";
        }
      }
      if (definition?.volunteeredLabel) {
        // if we are have volunteered for this shift, then use the volunteered prop
        if (
          (volunteeredForShift.value || volunteerDeclinedForShift.value) &&
          !isMyShift.value &&
          !wasMyShift.value
        ) {
          return definition.volunteeredLabel;
        }
      }
      if (definition[labelProperty]) {
        return definition[labelProperty];
      }
      if (definition[templateProperty]) {
        return definition[templateProperty]
          .replace("${displayName}", currentAssignment.value?.displayName)
          .replace("${prevDisplayName}", prevAssignment.value?.displayName);
      }
      return "";
    });

    return {
      authStore,
      coverShiftLabel,
      canShowCoverShiftButton,
      canSeeShift,
      canShowStatus,
      getShiftCoverRequestClass,
      getShiftCoverRequestIcon,
      getShiftCoverRequestText,
      volunteeredForShift,
      volunteerDeclinedForShift,
      formatModifiedDateLong,
      currentAssignment,
      isMyShift,
      wasMyShift,
      getShiftDate,
      isHighlighted: computed(() => {
        return window.location.hash === `#shift-${props.shift.id}`;
      }),
    };
  },
};
</script>

<style :lang="scss">
.shift .cover-button {
  min-width: 10em;
}
.fc-event :has(div.highlighted) {
  border: 1px solid var(--bs-primary) !important;
  box-shadow: 0 0 10px var(--bs-primary);
}
</style>
